import { QUERIES, WithChildren, WITPaginationState } from '@/_metronic/helpers'
import { BaseResponse } from '@/_metronic/helpers/crud-helper/base-list-response'
import { useQueryRequest } from '@/app/modules/report-trend-analysis/core/QueryRequestProvider'
import { FilterReportTrendAnalysis } from '@/app/modules/report-trend-analysis/core/_models'
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { FilterTop, initialFilter, RevenueStreamOutlet } from './_models'
import { getRevenueTopOutlet } from './_requests'

/**
 * Begin: Initial Values
 */
export const initialQueryResponse = {refetch: () => {}, isLoading: false, request: initialFilter}

/**
 * Begin: Response Context
 *
 * It used for context response wrapper, so the response can
 * called everywhere, as long as it wrapped inside the context
 */
type ProductQueryResponseContextProps = {
  response?: BaseResponse<Array<RevenueStreamOutlet>> | undefined
  refetch: () => void
  isLoading: boolean
  request: FilterTop
}

function createResponseContext<T>(initialState: ProductQueryResponseContextProps) {
  return createContext(initialState)
}

const QueryResponseContext = createResponseContext<Array<RevenueStreamOutlet>>(initialQueryResponse)

/**
 * Begin: The Provider
 */
const OutletQueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  
  // Convert FilterReportTrendAnalysis to FilterTop
  const convertToFilterTop = (filter: FilterReportTrendAnalysis): FilterTop => {
    return {
      set_sub_brand_id: filter.set_sub_brand_id,
      sub_brand_id: filter.sub_brand_id,
      set_brand_id: filter.set_brand_id,
      brand_id: filter.brand_id,
      set_area: filter.set_area,
      area: filter.area,
      set_region_id: filter.set_region_id,
      region_id: filter.region_id,
      set_city_id: filter.set_city_id,
      city_id: filter.city_id,
      set_outlet_id: filter.set_outlet_id,
      outlet_id: filter.outlet_id,
      set_month: !!filter.month && filter.month.length > 0,
      month: filter.month || [],
      set_year: !!filter.year,
      year: filter.year?.toString() || "",
    }
  }

  const convertedState = useMemo(() => convertToFilterTop(state), [state])
  const [request, setRequest] = useState<FilterTop>(convertedState)

  useEffect(() => {
    const requestStr = JSON.stringify(request)
    const updatedStr = JSON.stringify(convertedState)
    if (requestStr !== updatedStr) {
      setRequest(convertedState)
    }
  }, [convertedState, request])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.REVENUE_STREAM_OUTLET}-${JSON.stringify(request)}`,
    () => {
      return getRevenueTopOutlet(request)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, request}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useOutletQueryResponse = () => useContext(QueryResponseContext)

const useOutletQueryResponseData = () => {
  const {response} = useOutletQueryResponse()
  if (!response) {
    return null
  }

  return response?.response?.data || null
}

const useOutletQueryResponsePagination = () => {
  const defaultPaginationState: WITPaginationState = {
    page: 1,
    pageSize: 10,
    totalCount: 1,
  }

  const {response} = useOutletQueryResponse()
  if (!response || !response.response) {
    return defaultPaginationState
  }
  const res = response.response
  return {
    page: res.current_page,
    pageSize: res.limit,
    totalCount: res.total_data,
  } as WITPaginationState
}

const useOutletQueryResponseLoading = (): boolean => {
  const {isLoading} = useOutletQueryResponse()
  return isLoading
}

export {
  OutletQueryResponseProvider,
  useOutletQueryResponse,
  useOutletQueryResponseData,
  useOutletQueryResponseLoading,
  useOutletQueryResponsePagination
}

